import { Col } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType
} from '@amzn/stencil-react-components/message-banner';
import React, { useState } from 'react';
import { ErrorDetailsPanel } from 'src/components/ErrorDetailsPanel/ErrorDetailsPanel';
import { ExceptionDetail, toExceptionDetail } from 'src/models/ExceptionDetail';
import { FailureContext } from 'src/utils/ErrorRedirectUtils';

/**
 * Shared inline-error plumbing for the admin console's create/edit/add modals:
 * a single {@link ExceptionDetail} state with clear/capture helpers and a
 * standard render block (friendly message + collapsible {@link ErrorDetailsPanel}).
 * Centralised here so call sites don't re-hand-roll it and reintroduce the same
 * bugs (dismissing on failure, stale panel on reopen).
 *
 * `captureError` normalises both error shapes we see: builder errors are already
 * {@link ExceptionDetail}s, TRD/asset APIs rethrow the raw AxiosError.
 */
export interface UseInlineErrorResult {
  /** The captured exception, or undefined when there is no active error. */
  errorException: ExceptionDetail | undefined;
  /** Store a caught error (raw Axios or already-parsed) as the active error. */
  captureError: (error: unknown) => void;
  /**
   * Surface a client-side validation message inline (no backend exception),
   * e.g. "Please enter at least one employee ID." Renders in the same inline
   * error banner as captured backend errors.
   */
  setError: (message: string) => void;
  /**
   * Surface a custom headline (e.g. a batch summary such as "Failed to add:
   * 106376775.") plus, when a backend exception is supplied, the collapsible
   * details panel for that exception. Use for batch flows where the headline
   * is a summary but the underlying failure still has backend detail.
   */
  showError: (headline: string, backendError?: unknown) => void;
  /** Clear the active error (call on submit-start and on modal open). */
  clearError: () => void;
  /**
   * Render the standardised inline error: a friendly red message plus the
   * collapsible ErrorDetailsPanel. Returns null when there is no active error.
   * @param source - `context.source` for the panel, e.g. `'CreateUsersModal - createUser'`.
   * @param fallbackMessage - shown when the exception has no `errorMessage`.
   */
  renderError: (source: string, fallbackMessage: string) => JSX.Element | null;
}

const isExceptionDetail = (error: unknown): error is ExceptionDetail =>
  typeof error === 'object' &&
  error !== null &&
  ('errorCode' in error || 'errorStatus' in error || 'errorMessage' in error);

export const useInlineError = (): UseInlineErrorResult => {
  const [errorException, setErrorException] = useState<
    ExceptionDetail | undefined
  >(undefined);
  // True for a client-side validation message (setError): the details panel is
  // suppressed since there is no backend exception to expand.
  const [validationOnly, setValidationOnly] = useState(false);
  // Optional headline overriding the exception's own message (batch summaries).
  const [headline, setHeadline] = useState<string | undefined>();

  const captureError = (error: unknown) => {
    setValidationOnly(false);
    setHeadline(undefined);
    setErrorException(
      isExceptionDetail(error) ? error : toExceptionDetail(error)
    );
  };

  const clearError = () => {
    setValidationOnly(false);
    setHeadline(undefined);
    setErrorException(undefined);
  };

  const setError = (message: string) => {
    setValidationOnly(true);
    setHeadline(undefined);
    setErrorException({ errorMessage: message } as ExceptionDetail);
  };

  const showError = (headlineText: string, backendError?: unknown) => {
    setHeadline(headlineText);
    if (backendError !== undefined) {
      setValidationOnly(false);
      setErrorException(
        isExceptionDetail(backendError)
          ? backendError
          : toExceptionDetail(backendError)
      );
    } else {
      setValidationOnly(true);
      setErrorException({ errorMessage: headlineText } as ExceptionDetail);
    }
  };

  const renderError = (source: string, fallbackMessage: string) => {
    if (!errorException) {
      return null;
    }
    const context: Partial<FailureContext> = { source };
    return (
      <Col gridGap={'S100'}>
        <MessageBanner type={MessageBannerType.Error}>
          {headline || errorException.errorMessage || fallbackMessage}
        </MessageBanner>
        {validationOnly ? null : (
          <ErrorDetailsPanel exception={errorException} context={context} />
        )}
      </Col>
    );
  };

  return {
    errorException,
    captureError,
    setError,
    showError,
    clearError,
    renderError
  };
};

export default useInlineError;
